import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NoteIcon from '@mui/icons-material/Note';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './HomePage.css';

const PRIMARY = '#6366f1';
const DARK_NAV = 'var(--bg-surf)';

const modules = [
  {
    id: 'expense-tracker',
    name: 'Expense Tracker',
    description: 'Track and analyze your spending habits with detailed insights and category breakdowns.',
    path: '/expense/upload',
    available: true,
    Icon: AccountBalanceIcon,
    gradient: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
  },
  {
    id: 'workout-tracker',
    name: 'Workout Tracker',
    description: 'Log your lifting progression, track PRs, and monitor your fitness journey over time.',
    path: '/workout/log',
    available: true,
    Icon: FitnessCenterIcon,
    gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  },
  {
    id: 'investment-tracker',
    name: 'Investment Tracker',
    description: 'Log transactions and view your portfolio holdings, performance, and allocation.',
    path: '/investments/holdings',
    available: true,
    Icon: ShowChartIcon,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  },
  {
    id: 'career-mapper',
    name: 'Career Mapper',
    description: 'Visualise career journeys as interactive graphs. Parse LinkedIn profiles with AI and track career progression across versions.',
    path: '/career',
    available: true,
    Icon: AccountTreeIcon,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
  },
  {
    id: 'interview-prep',
    name: 'Interview Prep',
    description: 'Build a personal bank of interview stories. Upload documents or paste text — AI extracts and structures your stories in full narrative and interview-ready bullet form.',
    path: '/interview',
    available: true,
    Icon: QuestionAnswerOutlinedIcon,
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
  },
  {
    id: 'ai-chat',
    name: 'AI Chat',
    description: 'Chat with OpenAI, Anthropic, Google Gemini, or Amazon Bedrock. System prompts, multi-turn conversations, and markdown rendering.',
    path: '/chat',
    available: true,
    Icon: SmartToyIcon,
    gradient: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
  },
  {
    id: 'journal',
    name: 'Journal',
    description: 'Write daily journal entries in markdown. Toggle between edit and preview. Open the AI counsellor to get thoughtful reflections on what you\'ve written.',
    path: '/journal',
    available: true,
    Icon: AutoStoriesIcon,
    gradient: 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
  },
  {
    id: 'tracker',
    name: 'Tracker',
    description: 'Visual node-edge workflow tracker. Initiatives → Opportunities → Activities → Tasks → Artifacts. Left-to-right flow map with custom fields and edge types.',
    path: '/tracker',
    available: true,
    Icon: AccountTreeIcon,
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
  },
  {
    id: 'rehearsals',
    name: 'Rehearsals',
    description: 'Practice mock interviews with an AI interviewer — voice only. Add JDs and context, pick an interview type (Amazon Loop, Google RRK, GCA, G&L, and more), and rehearse.',
    path: '/rehearsals',
    available: true,
    Icon: RecordVoiceOverIcon,
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
  },
  {
    id: 'task-manager',
    name: 'Task Manager',
    description: 'Manage tasks and projects to stay organised and productive every day.',
    path: '/tasks',
    available: false,
    Icon: CheckCircleOutlineIcon,
    gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
  },
  {
    id: 'health-tracker',
    name: 'Health Tracker',
    description: 'Monitor your health metrics, sleep, and wellness trends in one place.',
    path: '/health',
    available: false,
    Icon: FavoriteIcon,
    gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
  },
  {
    id: 'notes',
    name: 'Notes',
    description: 'Capture ideas, plans, and personal notes with a clean writing experience.',
    path: '/notes',
    available: false,
    Icon: NoteIcon,
    gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
  },
];

function ModuleCard({ module, onClick }) {
  const { name, description, available, Icon, gradient } = module;

  const cardContent = (
    <CardContent
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Icon badge */}
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2,
          background: available ? gradient : 'linear-gradient(135deg, #cbd5e1 0%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: available ? '0 4px 14px rgba(99,102,241,0.35)' : 'none',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: '#fff', fontSize: 26 }} />
      </Box>

      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: available ? 'var(--tx-hi)' : 'var(--tx-ft)',
            fontSize: '1.05rem',
            lineHeight: 1.3,
          }}
        >
          {name}
        </Typography>
        {!available && (
          <Chip
            label="Coming Soon"
            size="small"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              bgcolor: 'rgba(255,255,255,0.06)',
              color: 'var(--tx-ft)',
              height: 20,
            }}
          />
        )}
      </Box>

      {/* Description */}
      <Typography
        variant="body2"
        sx={{
          color: available ? 'var(--tx-lo)' : 'var(--tx-ft)',
          lineHeight: 1.65,
          flexGrow: 1,
        }}
      >
        {description}
      </Typography>

      {/* CTA row */}
      {available && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: PRIMARY,
            mt: 'auto',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            Open module
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
    </CardContent>
  );

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: available ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
        bgcolor: '#131929',
        opacity: available ? 1 : 0.5,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ...(available && {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 32px rgba(99,102,241,0.15)',
            borderColor: 'rgba(99,102,241,0.35)',
          },
        }),
      }}
    >
      {available ? (
        <CardActionArea
          onClick={onClick}
          sx={{ height: '100%', alignItems: 'flex-start', borderRadius: 3 }}
        >
          {cardContent}
        </CardActionArea>
      ) : (
        <Box sx={{ cursor: 'not-allowed', height: '100%' }}>{cardContent}</Box>
      )}
    </Card>
  );
}

function HomePage() {
  const navigate = useNavigate();

  const handleModuleClick = (module) => {
    if (module.available) {
      navigate(module.path);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DARK_NAV }}>
      {/* Hero header */}
      <Box
        sx={{
          pt: { xs: 7, md: 10 },
          pb: { xs: 8, md: 11 },
          px: 2,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(99,102,241,0.28) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              color: PRIMARY,
              fontWeight: 700,
              letterSpacing: 3,
              fontSize: '0.72rem',
              display: 'block',
              mb: 2,
            }}
          >
            Slate Pro
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.15,
              mb: 2.5,
            }}
          >
            Welcome to{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(90deg, #818cf8 0%, #a5b4fc 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              LifeHub
            </Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'var(--tx-lo)',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: 520,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Your personal super-app for finance, fitness, and life management — all in one place.
          </Typography>
        </Container>
      </Box>

      {/* Module cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 4 } }}>
        <Typography
          variant="overline"
          sx={{
            color: 'var(--tx-ft)',
            fontWeight: 700,
            letterSpacing: 2.5,
            fontSize: '0.68rem',
            display: 'block',
            mb: 3,
          }}
        >
          Your Modules
        </Typography>

        <Grid container spacing={3}>
          {modules.map((module) => (
            <Grid
              key={module.id}
              size={{ xs: 12, sm: 6, md: 4 }}
            >
              <ModuleCard
                module={module}
                onClick={() => handleModuleClick(module)}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default HomePage;
